import { Dumbbell, Globe, Layers, Lock, Pause, type LucideIcon } from 'lucide-react'
import { useLocale } from '../../i18n/context'

// ── Status vocabulary (Wave 4 §4) ─────────────────────────────────────────────
// All content statuses render through one badge component: same shape, same type
// scale, icon from the icon system, neutral color. No emoji (Wave 4 §1).
// "Excluded from stream" is NOT a degraded state — the badge is informational,
// not punishing (Wave 4 §4: full visual weight is preserved on the card).

// `practice` is a session status, not a deck one (ADR-112 Decision 6): a practice
// session, in which no answer changes a word's level. It joins this vocabulary
// rather than getting a badge of its own — one status component for all statuses.
export type DeckStatus = 'paused' | 'readonly' | 'public' | 'system' | 'practice'

const STATUS_ICON: Record<DeckStatus, LucideIcon> = {
  paused:   Pause,
  readonly: Lock,
  public:   Globe,
  system:   Layers,
  practice: Dumbbell,
}

interface Props {
  status: DeckStatus
  size?: 'sm' | 'md'
  /** Suppress the text label — icon only (still has aria-label for a11y). */
  iconOnly?: boolean
  className?: string
}

/**
 * Neutral informational status badge for deck metadata (Wave 4 §4).
 *
 * Covers: excluded from daily learning, read-only, public, system deck, and a
 * practice session (Learn top bar, ADR-112).
 * Shape: pill with icon + label. Color: --text-secondary on --bg-secondary.
 * No red — these are states, not errors (Wave 3 §3).
 */
export default function DeckStatusBadge({ status, size = 'sm', iconOnly = false, className }: Props) {
  const { tr } = useLocale()
  const Icon = STATUS_ICON[status]
  const label = tr.deckStatus[status]
  const isSm = size === 'sm'

  return (
    <span
      aria-label={label}
      title={label}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isSm ? 3 : 4,
        padding: isSm ? '2px 7px' : '3px 9px',
        background: 'var(--bg-secondary)',
        color: 'var(--text-secondary)',
        borderRadius: 'var(--radius-pill)',
        fontSize: isSm ? 11 : 12,
        fontWeight: 500,
        fontFamily: 'var(--font-ui)',
        lineHeight: 1.6,
        flexShrink: 0,
        userSelect: 'none',
        border: '1px solid var(--border)',
      }}
    >
      <Icon size={isSm ? 10 : 12} strokeWidth={2} aria-hidden="true" />
      {!iconOnly && <span>{label}</span>}
    </span>
  )
}
